import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CommercialApiService,
  CommercialModuleDefinition,
  CommercialPlan,
  CommercialPlanModuleAssignment
} from './commercial-api.service';

interface ModuleMatrixRow {
  moduleKey: string;
  moduleName: string;
  availability: string;
}

@Component({
  standalone: true,
  selector: 'ain-commercial-plans',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CheckboxModule,
    OfferingSelectorComponent
  ],
  template: `
    <ain-page [title]="t('commercial.plans.title')" [description]="t('commercial.plans.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'commercial.plans.help.title'"
        [descriptionKey]="'commercial.plans.help.description'"
        [bulletKeys]="[
          'commercial.plans.help.bullets.matrix',
          'commercial.plans.help.bullets.projection'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('commercial.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('commercial.errorLoad')" [message]="error() ?? ''" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('commercial.actions.refresh')" (click)="load()"></button>
            <button
              pButton
              type="button"
              [label]="t('commercial.plans.edit')"
              [disabled]="!selectedPlan()"
              (click)="openEdit()"></button>
            <button
              pButton
              type="button"
              severity="secondary"
              [label]="t('commercial.actions.syncProjection')"
              [disabled]="!selectedPlan() || syncing()"
              (click)="syncSelected()"></button>
          </div>
          @if (info()) {
            <p class="info">{{ info() }}</p>
          }
          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('commercial.plans.empty')" />
          } @else {
            <p-table
              [value]="rows()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              selectionMode="single"
              [selection]="selectedPlan()"
              (selectionChange)="onPlanSelected($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"></th>
                  <th>{{ t('commercial.plans.fields.code') }}</th>
                  <th>{{ t('commercial.plans.fields.name') }}</th>
                  <th>{{ t('commercial.plans.fields.tierRank') }}</th>
                  <th>{{ t('commercial.plans.fields.pricingModel') }}</th>
                  <th>{{ t('commercial.plans.fields.publiclySellable') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr [pSelectableRow]="row">
                  <td><p-tableRadioButton [value]="row" /></td>
                  <td>{{ row.code }}</td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.tierRank ?? '—' }}</td>
                  <td>{{ pricingModelLabel(row.pricingModel) }}</td>
                  <td>{{ row.isPubliclySellable ? t('common.yes') : t('common.no') }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }

      <p-dialog [header]="editTitle()" [(visible)]="editVisible" [modal]="true" [style]="{ width: '760px' }">
        <div class="form-grid">
          <label>
            <span>{{ t('commercial.plans.fields.tierRank') }}</span>
            <p-inputNumber [(ngModel)]="editForm.tierRank" [useGrouping]="false" />
          </label>
          <label>
            <span>{{ t('commercial.plans.fields.pricingModel') }}</span>
            <p-select
              [options]="pricingModelOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="editForm.pricingModel"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ pricingModelOptionLabel(item) }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ pricingModelOptionLabel(item) }}</ng-template>
            </p-select>
          </label>
          <label class="checkbox-row full">
            <p-checkbox binary="true" [(ngModel)]="editForm.isPubliclySellable" />
            <span>{{ t('commercial.plans.fields.publiclySellable') }}</span>
          </label>
          <label class="full">
            <span>{{ t('commercial.plans.fields.catalogOffering') }}</span>
            <ain-offering-selector [(ngModel)]="editForm.catalogOfferingId" [showClear]="true" />
          </label>
        </div>

        <section class="matrix">
          <h3>
            {{ t('commercial.plans.moduleMatrix.title') }}
            <ain-info-hint
              [titleKey]="'commercial.plans.moduleMatrix.help.title'"
              [descriptionKey]="'commercial.plans.moduleMatrix.help.description'"
              [bulletKeys]="['commercial.plans.moduleMatrix.help.bullets.availability']" />
          </h3>
          @if (matrixRows().length === 0) {
            <p class="muted">{{ t('commercial.plans.moduleMatrix.empty') }}</p>
          } @else {
            <p-table [value]="matrixRows()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('commercial.plans.moduleMatrix.module') }}</th>
                  <th>{{ t('commercial.plans.moduleMatrix.availability') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.moduleName }} ({{ row.moduleKey }})</td>
                  <td>
                    <p-select
                      [options]="availabilityOptions"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="row.availability"
                      appendTo="body">
                      <ng-template pTemplate="selectedItem" let-item>{{ availabilityOptionLabel(item) }}</ng-template>
                      <ng-template pTemplate="item" let-item>{{ availabilityOptionLabel(item) }}</ng-template>
                    </p-select>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        @if (editError()) {
          <p class="error">{{ editError() }}</p>
        }
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="editVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveEdit()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid var(--ain-color-border, #e5e7eb); border-radius: 14px; padding: 16px; background: var(--ain-color-surface); }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
    .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
    .form-grid label { display: flex; flex-direction: column; gap: 6px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .checkbox-row { flex-direction: row !important; align-items: center; gap: 8px !important; }
    .matrix h3 { display: flex; align-items: center; gap: 8px; margin: 0 0 12px; font-size: 1rem; }
    .info, .muted { margin: 0 0 12px; color: var(--ain-color-text-muted, #64748b); }
    .error { color: #b91c1c; margin-top: 8px; }
  `]
})
export class CommercialPlansComponent implements OnInit {
  private readonly api = inject(CommercialApiService);
  private readonly localizationService = inject(LocalizationService);

  readonly rows = signal<CommercialPlan[]>([]);
  readonly modules = signal<CommercialModuleDefinition[]>([]);
  readonly matrixRows = signal<ModuleMatrixRow[]>([]);
  readonly selectedPlan = signal<CommercialPlan | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);
  readonly saving = signal(false);
  readonly syncing = signal(false);
  readonly editError = signal<string | null>(null);
  readonly editTitle = signal('');

  editVisible = false;
  editForm = {
    tierRank: null as number | null,
    isPubliclySellable: false,
    pricingModel: 'Fixed',
    catalogOfferingId: null as string | null
  };

  readonly pricingModelOptions = [
    { value: 'Fixed', labelKey: 'commercial.plans.pricingModel.fixed', label: '' },
    { value: 'CustomQuote', labelKey: 'commercial.plans.pricingModel.customQuote', label: '' }
  ];

  readonly availabilityOptions = [
    { value: 'Included', labelKey: 'commercial.plans.availability.included', label: '' },
    { value: 'OptionalAddOn', labelKey: 'commercial.plans.availability.optionalAddOn', label: '' },
    { value: 'Unavailable', labelKey: 'commercial.plans.availability.unavailable', label: '' }
  ];

  ngOnInit(): void {
    this.refreshSelectOptionLabels();
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listPlans().subscribe({
      next: items => {
        this.rows.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('commercial.plans.errorLoad'));
        this.loading.set(false);
      }
    });

    this.api.listModules().subscribe({
      next: items => this.modules.set(items)
    });
  }

  onPlanSelected(plan: CommercialPlan | CommercialPlan[] | null): void {
    const selected = Array.isArray(plan) ? plan[0] ?? null : plan;
    this.selectedPlan.set(selected);
  }

  openEdit(): void {
    const plan = this.selectedPlan();
    if (!plan) {
      return;
    }

    this.editForm = {
      tierRank: plan.tierRank,
      isPubliclySellable: plan.isPubliclySellable,
      pricingModel: plan.pricingModel,
      catalogOfferingId: plan.catalogOfferingId
    };
    this.matrixRows.set(this.buildMatrix(plan));
    this.editTitle.set(`${this.t('commercial.plans.edit')}: ${plan.name}`);
    this.editError.set(null);
    this.editVisible = true;
  }

  saveEdit(): void {
    const plan = this.selectedPlan();
    if (!plan) {
      return;
    }

    const moduleAssignments: CommercialPlanModuleAssignment[] = this.matrixRows()
      .filter(row => row.availability !== 'Unavailable')
      .map(row => ({ moduleKey: row.moduleKey, availability: row.availability }));

    this.saving.set(true);
    this.editError.set(null);
    this.api.updatePlan(plan.id, {
      tierRank: this.editForm.tierRank,
      isPubliclySellable: this.editForm.isPubliclySellable,
      pricingModel: this.editForm.pricingModel,
      catalogOfferingId: this.editForm.catalogOfferingId,
      moduleAssignments
    }).subscribe({
      next: updated => {
        this.saving.set(false);
        this.editVisible = false;
        this.info.set(this.t('commercial.plans.saved'));
        this.rows.update(items => items.map(item => item.id === updated.id ? updated : item));
        this.selectedPlan.set(updated);
      },
      error: (err: HttpErrorResponse) => {
        this.saving.set(false);
        this.editError.set(this.resolveError(err, 'commercial.plans.errorSave'));
      }
    });
  }

  syncSelected(): void {
    const plan = this.selectedPlan();
    if (!plan) {
      return;
    }

    this.syncing.set(true);
    this.info.set(null);
    this.api.syncProjection({ sourceType: 'Plan', sourceId: plan.id }).subscribe({
      next: result => {
        this.syncing.set(false);
        this.info.set(this.t('commercial.projection.synced').replace('{status}', result.projectionStatus));
      },
      error: () => {
        this.syncing.set(false);
        this.info.set(this.t('commercial.projection.error'));
      }
    });
  }

  pricingModelLabel(model: string): string {
    const match = this.pricingModelOptions.find(item => item.value === model);
    return match ? this.t(match.labelKey) : model;
  }

  pricingModelOptionLabel(item: unknown): string {
    const option = this.resolveSelectOption(this.pricingModelOptions, item);
    if (!option) {
      return typeof item === 'string' ? item : '';
    }

    return option.label;
  }

  availabilityOptionLabel(item: unknown): string {
    const option = this.resolveSelectOption(this.availabilityOptions, item);
    if (!option) {
      return typeof item === 'string' ? item : '';
    }

    return option.label;
  }

  private refreshSelectOptionLabels(): void {
    this.pricingModelOptions.forEach(option => {
      option.label = this.tWithFallback(option.labelKey, this.defaultLabel(option.value));
    });
    this.availabilityOptions.forEach(option => {
      option.label = this.tWithFallback(option.labelKey, this.defaultLabel(option.value));
    });
  }

  private tWithFallback(key: string, fallback: string): string {
    const translated = this.t(key);
    return translated === key ? fallback : translated;
  }

  private defaultLabel(value: string): string {
    switch (value) {
      case 'Fixed':
        return 'قیمت ثابت';
      case 'CustomQuote':
        return 'پیشنهاد اختصاصی';
      case 'Included':
        return 'شامل';
      case 'OptionalAddOn':
        return 'افزونه اختیاری';
      case 'Unavailable':
        return 'غیرفعال';
      default:
        return value;
    }
  }

  private resolveSelectOption(
    options: ReadonlyArray<{ value: string; labelKey: string; label: string }>,
    item: unknown
  ): { value: string; labelKey: string; label: string } | null {
    if (typeof item === 'string') {
      return options.find(option => option.value === item) ?? null;
    }

    const record = item as { value?: string; label?: string } | null;
    if (!record) {
      return null;
    }

    if (record.value) {
      return options.find(option => option.value === record.value) ?? null;
    }

    if (record.label) {
      return options.find(option => option.label === record.label) ?? null;
    }

    return null;
  }

  private buildMatrix(plan: CommercialPlan): ModuleMatrixRow[] {
    const assignmentMap = new Map(plan.moduleAssignments.map(item => [item.moduleKey, item.availability]));
    return this.modules().map(module => ({
      moduleKey: module.moduleKey,
      moduleName: module.name,
      availability: assignmentMap.get(module.moduleKey) ?? 'Unavailable'
    }));
  }

  private resolveError(error: HttpErrorResponse, fallbackKey: string): string {
    if (error.status === 400) {
      return this.t('commercial.errors.invalid');
    }
    return this.t(fallbackKey);
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
